import type { Todo } from "../../domain/entities.ts";
import { CALENDAR_SLOT_MINUTES, localSlotInstant, taskDurationMinutes, type CalendarPlacement } from "../tasks/calendar-presentation.ts";
import { dateInTimezone } from "../tasks/recurrence.ts";
import { resolveWallTime, wallTimeInZone } from "../tasks/task-time.ts";

export type CalendarScheduleField = "startDate" | "startAt" | "endAt" | "wallStartAt" | "wallEndAt" | "estimatedMinutes";

export interface CalendarSchedulingCapabilities<Snapshot> {
  tasks(): Todo[];
  canEdit(id: string): boolean;
  schedulingTaskId(): string | null;
  setSchedulingTaskId(id: string | null): void;
  openComposer(slot: CalendarPlacement): Promise<void>;
  snapshot(todo: Todo): Snapshot;
  mutate(id: string, update: (draft: Todo) => void): Promise<unknown>;
  render(): Promise<void>;
  undo(message: string, rootId: string, before: Snapshot[], fields: CalendarScheduleField[]): void;
  haptic(): void;
  sync(): void;
  formatDate(date: string): string;
  timezone(): string;
}

export interface CalendarSchedulingController {
  activateDate(date: string): Promise<void>;
  activateSlot(slot: HTMLElement): Promise<void>;
  scheduleAt(id: string, date: string, minute: number): Promise<void>;
  scheduleAllDay(id: string, date: string): Promise<void>;
  shift(id: string, minutes: number): Promise<void>;
  resize(id: string, minutes: number): Promise<void>;
}

export function createCalendarSchedulingController<Snapshot>(capabilities: CalendarSchedulingCapabilities<Snapshot>): CalendarSchedulingController {
  const task = (id: string) => capabilities.tasks().find(item => item.id === id);
  const finish = async () => { capabilities.setSchedulingTaskId(null); capabilities.haptic(); await capabilities.render(); };
  const scheduleAt = async (id: string, date: string, minute: number) => {
    const todo = task(id);
    if (!todo || !capabilities.canEdit(id)) return;
    const before = capabilities.snapshot(todo);
    const duration = taskDurationMinutes(todo.startAt, todo.endAt, todo.estimatedMinutes);
    const startAt = localSlotInstant(date, minute);
    const endAt = new Date(new Date(startAt).getTime() + duration * 60_000).toISOString();
    await capabilities.mutate(id, draft => {
      if (draft.scheduleMode === "floating" && draft.scheduleTimeZone) {
        draft.wallStartAt = wallTimeInZone(startAt, draft.scheduleTimeZone);
        draft.wallEndAt = wallTimeInZone(endAt, draft.scheduleTimeZone);
        draft.startAt = resolveWallTime(draft.wallStartAt, draft.scheduleTimeZone);
        draft.endAt = resolveWallTime(draft.wallEndAt, draft.scheduleTimeZone);
        draft.startDate = draft.wallStartAt.slice(0, 10);
      } else { draft.startDate = date; draft.startAt = startAt; draft.endAt = endAt; }
      draft.estimatedMinutes = duration;
    });
    await finish();
    capabilities.undo(`Scheduled ${capabilities.formatDate(date)} · ${new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(startAt))}`, todo.rootId, [before], ["startDate", "startAt", "endAt", "wallStartAt", "wallEndAt", "estimatedMinutes"]);
    capabilities.sync();
  };
  const controller: CalendarSchedulingController = {
    activateDate: async date => {
      const selected = capabilities.schedulingTaskId();
      if (selected) await controller.scheduleAllDay(selected, date);
      else await capabilities.openComposer({ date });
    },
    activateSlot: async slot => {
      const date = slot.dataset.date!;
      const minute = Number(slot.dataset.minute);
      const selected = capabilities.schedulingTaskId();
      if (selected) await scheduleAt(selected, date, minute);
      else await capabilities.openComposer({ date, minute });
    },
    scheduleAt,
    scheduleAllDay: async (id, date) => {
      const todo = task(id);
      if (!todo || !capabilities.canEdit(id)) return;
      const before = capabilities.snapshot(todo);
      await capabilities.mutate(id, draft => { draft.startDate = date; draft.startAt = null; draft.endAt = null; draft.wallStartAt = null; draft.wallEndAt = null; });
      await finish();
      capabilities.undo(`Moved to ${capabilities.formatDate(date)}`, todo.rootId, [before], ["startDate", "startAt", "endAt", "wallStartAt", "wallEndAt"]);
      capabilities.sync();
    },
    shift: async (id, minutes) => {
      const todo = task(id);
      if (!todo?.startAt || !capabilities.canEdit(id)) return;
      const before = capabilities.snapshot(todo);
      await capabilities.mutate(id, draft => {
        draft.startAt = new Date(new Date(draft.startAt!).getTime() + minutes * 60_000).toISOString();
        if (draft.endAt) draft.endAt = new Date(new Date(draft.endAt).getTime() + minutes * 60_000).toISOString();
        if (draft.scheduleMode === "floating" && draft.scheduleTimeZone) {
          draft.wallStartAt = wallTimeInZone(draft.startAt, draft.scheduleTimeZone);
          draft.wallEndAt = draft.endAt ? wallTimeInZone(draft.endAt, draft.scheduleTimeZone) : null;
          draft.startAt = resolveWallTime(draft.wallStartAt, draft.scheduleTimeZone);
          draft.endAt = draft.wallEndAt ? resolveWallTime(draft.wallEndAt, draft.scheduleTimeZone) : null;
          draft.startDate = draft.wallStartAt.slice(0, 10);
        } else draft.startDate = dateInTimezone(new Date(draft.startAt), capabilities.timezone());
      });
      await capabilities.render();
      capabilities.undo(`Task moved ${Math.abs(minutes)} minutes ${minutes < 0 ? "earlier" : "later"}`, todo.rootId, [before], ["startDate", "startAt", "endAt", "wallStartAt", "wallEndAt"]);
      capabilities.sync();
    },
    resize: async (id, minutes) => {
      const todo = task(id);
      if (!todo?.startAt || !capabilities.canEdit(id)) return;
      const before = capabilities.snapshot(todo);
      const duration = Math.max(CALENDAR_SLOT_MINUTES, taskDurationMinutes(todo.startAt, todo.endAt, todo.estimatedMinutes) + minutes);
      await capabilities.mutate(id, draft => { draft.estimatedMinutes = duration; draft.endAt = new Date(new Date(draft.startAt!).getTime() + duration * 60_000).toISOString(); if (draft.scheduleMode === "floating" && draft.scheduleTimeZone) { draft.wallEndAt = wallTimeInZone(draft.endAt, draft.scheduleTimeZone); draft.endAt = resolveWallTime(draft.wallEndAt, draft.scheduleTimeZone); } });
      await capabilities.render();
      capabilities.undo(`Task duration set to ${duration} minutes`, todo.rootId, [before], ["endAt", "wallEndAt", "estimatedMinutes"]);
      capabilities.sync();
    },
  };
  return controller;
}
